import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApiTeamsGet200ResponseInnerSubscription } from '../client';
import { useApi } from '../state/ApiContext';
import {
  planDaysUntil,
  planShortDate,
  TEAM_SUBSCRIPTION_CHANGED_EVENT,
} from './plan-shared';
import './TeamStatusBanner.css';

const TEAM_PATH_PATTERN = /^\/console\/team\/(\d+)(?:\/|$)/;

const DAY_MS = 24 * 60 * 60 * 1000;

/** True once a past_due subscription is more than one day beyond its renewal. */
function pastDueBeyondOneDay(
  subscription: ApiTeamsGet200ResponseInnerSubscription,
  now: number,
): boolean {
  if (subscription.status !== 'past_due') return false;
  if (!subscription.currentPeriodEnd) return true;
  return now - new Date(subscription.currentPeriodEnd).getTime() > DAY_MS;
}

/**
 * Full-bleed tint, page-width text. The strip spans the viewport so it reads
 * as chrome, while the inner box matches the route's PageShell (like the
 * header does) so the message starts where the page content starts.
 */
const Banner: React.FC<{
  tone: 'trial' | 'warning';
  testId: string;
  children: React.ReactNode;
}> = ({ tone, testId, children }) => (
  <div
    className={`team-status-banner team-status-banner-${tone}`}
    data-testid={testId}>
    <div className="team-status-banner-content">{children}</div>
  </div>
);

/**
 * Site-wide subscription standing, pinned between the header and the page on
 * every team-scoped route: the free trial (with days remaining), an ended
 * trial, and a subscription more than a day past due. Sourced from the teams
 * list so every member sees it regardless of billing permission grants.
 */
const TeamStatusBanner: React.FC = () => {
  const location = useLocation();
  const { teamApi } = useApi();
  const [subscription, setSubscription] =
    useState<ApiTeamsGet200ResponseInnerSubscription | null>(null);
  const [changeTick, setChangeTick] = useState(0);

  const teamId = location.pathname.match(TEAM_PATH_PATTERN)?.[1] ?? null;

  // Plan pages announce subscription mutations so the banner reflects a
  // subscribe or pay-now immediately, not on the next navigation.
  useEffect(() => {
    const onChanged = () => setChangeTick((tick) => tick + 1);
    window.addEventListener(TEAM_SUBSCRIPTION_CHANGED_EVENT, onChanged);
    return () =>
      window.removeEventListener(TEAM_SUBSCRIPTION_CHANGED_EVENT, onChanged);
  }, []);

  useEffect(() => {
    if (!teamId) {
      setSubscription(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await teamApi.apiTeamsGet();
        if (cancelled) return;
        const team = (response.data ?? []).find(
          (candidate) => String(candidate.id) === teamId,
        );
        setSubscription(team?.subscription ?? null);
      } catch {
        if (!cancelled) setSubscription(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamApi, teamId, location.pathname, changeTick]);

  if (!teamId || !subscription) return null;

  if (subscription.status === 'trialing') {
    const daysLeft = planDaysUntil(subscription.trialEndsAt);
    return (
      <Banner tone="trial" testId="team-status-banner-trial">
        <strong>Free trial</strong>
        {daysLeft != null &&
          ` — ${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
        . <Link to={`/console/team/${teamId}/billing/plan`}>Choose a plan</Link>
      </Banner>
    );
  }

  if (subscription.status === 'trial_expired') {
    return (
      <Banner tone="warning" testId="team-status-banner-trial-expired">
        <strong>Free trial ended</strong> — agents on this team are paused.{' '}
        <Link to={`/console/team/${teamId}/billing/plan`}>Choose a plan</Link>{' '}
        to resume.
      </Banner>
    );
  }

  if (pastDueBeyondOneDay(subscription, Date.now())) {
    return (
      <Banner tone="warning" testId="team-status-banner-past-due">
        <strong>Payment failed</strong> — this team&apos;s subscription is past
        due. We retry the card daily
        {subscription.graceExpiresAt
          ? `; agents pause ${planShortDate(subscription.graceExpiresAt)}`
          : ''}
        .{' '}
        <Link to={`/console/team/${teamId}/billing`}>
          Review on the billing page
        </Link>
      </Banner>
    );
  }

  return null;
};

export default TeamStatusBanner;
